import React from 'react';
import type { ISODateRange } from 'shared/src/date-string-utils/types';
import { useCubeQuery } from '@/components/display/use-cube-query';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import { MetricSkeleton } from '@/components/feedback/metric-skeleton';
import { ActivePacingMetric, ExpiredPacingMetric, PendingPacingMetric } from './pacing-metrics';
import { getDateRangeState, getEffectiveDateRange } from './metric-date-utils';

type SpendPacingMetricProps = {
  dateRange: ISODateRange;
};

export function SpendPacingMetric({ dateRange }: SpendPacingMetricProps) {
  const { lineItem } = useLineItemContext();
  const state = getDateRangeState(dateRange);
  const [effectiveFrom, effectiveTo] = getEffectiveDateRange(dateRange, state);

  const result = useCubeQuery({
    measures: [
      'line_item_metrics_daily.spend_pacing',
      'line_item_metrics_daily.actual_spend',
      'line_item_metrics_daily.planned_spend'
    ],
    timeDimensions: [
      {
        dimension: 'line_item_metrics_daily.day',
        dateRange: [effectiveFrom, effectiveTo]
      }
    ],
    filters: [
      {
        member: 'line_item_metrics_daily.line_item_id',
        operator: 'equals',
        values: [lineItem.id]
      }
    ]
  });

  if (result.status === 'loading') return <MetricSkeleton />;
  if (result.status === 'error') return null;

  if (state === 'pending' || result.rows.length === 0) {
    return <PendingPacingMetric title="Spend" detail="$0.00 / $0 on-pace" />;
  }

  const {
    'line_item_metrics_daily.spend_pacing': pacing,
    'line_item_metrics_daily.actual_spend': actualSpend,
    'line_item_metrics_daily.planned_spend': plannedSpend
  } = result.formattedRows[0];

  if (state === 'active') {
    return (
      <ActivePacingMetric
        title="Spend"
        pacing={pacing}
        detail={`${actualSpend} / ${plannedSpend} on-pace`}
      />
    );
  }

  return (
    <ExpiredPacingMetric
      title="Spend"
      pacing={pacing}
      detail={`${actualSpend} of ${plannedSpend} planned`}
    />
  );
}
